import React, { useCallback, useEffect, useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import Box from '@material-ui/core/Box'
import FormControl from '@material-ui/core/FormControl'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import InputLabel from '@material-ui/core/InputLabel'
import MenuItem from '@material-ui/core/MenuItem'
import Radio from '@material-ui/core/Radio'
import RadioGroup from '@material-ui/core/RadioGroup'
import Select from '@material-ui/core/Select'
import Typography from '@material-ui/core/Typography'
import { makeStyles } from '@material-ui/core/styles'
import { useStyles } from 'pages/pga-coach/bookings/events/edit/edit-event-form/styles'

const useHostOptionStyles = makeStyles(theme => ({
  optionsColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    width: '100%',
    paddingTop: theme.spacing(1),
  },
  optionLabel: {
    alignItems: 'flex-start',
    margin: 0,
    '& .MuiFormControlLabel-label': {
      marginTop: 2,
      marginLeft: theme.spacing(0.5),
      paddingTop: theme.spacing(0.25),
    },
  },
  optionTitle: {
    fontSize: '1rem',
    fontWeight: 400,
    color: theme.palette.text.primary,
    lineHeight: 1.25,
  },
  optionDescription: {
    fontSize: '0.8125rem',
    color: theme.palette.text.secondary,
    lineHeight: 1.5,
    marginTop: theme.spacing(1),
  },
  radio: {
    padding: theme.spacing(1),
    marginTop: -2,
    color: '#70787C',
    '&.Mui-checked': {
      color: '#0053FF',
    },
  },
}))

const HOST_OPTIONS = [
  {
    value: 'individual',
    title: 'Individual coach',
    description: 'You are hosting this event as an individual coach.',
  },
  {
    value: 'coachingCenter',
    title: 'Coaching center',
    description: 'This event is hosted through your coaching center.',
  },
]

const CreateEventLocation = ({ coachFacilities = [], coachAcademies = [] }) => {
  const classes = useStyles()
  const hostOptionClasses = useHostOptionStyles()
  const { errors, control, watch, register, setValue } = useFormContext()
  const hasAcademies = coachAcademies.length >= 1
  const academyRequired = watch('academyRequired')
  const academyId = watch('academyId')
  const isAcademyEvent = !!academyRequired
  const [facilities, setFacilities] = useState(coachFacilities ?? [])

  const findAcademyById = useCallback(
    id => coachAcademies.find(academy => academy.id === id),
    [coachAcademies],
  )

  useEffect(() => {
    register('academyRequired')
  }, [register])

  useEffect(() => {
    if (academyId) {
      const academy = findAcademyById(academyId)
      setFacilities(academy?.facilities ?? [])
    } else {
      setFacilities(coachFacilities ?? [])
    }
  }, [academyId, coachFacilities, findAcademyById])

  const handleHostChange = event => {
    const isAcademy = event.target.value === 'coachingCenter'
    setValue('academyRequired', isAcademy)
    if (!isAcademy) {
      setValue('academyId', '')
      setValue('assistantCoachIds', [])
    }
  }

  const fieldGroup = (heading, helper, field) => (
    <Box className={classes.fieldGroup}>
      <Typography className={classes.sectionHeading}>{heading}</Typography>
      {helper ? (
        <Typography className={classes.fieldHelper}>{helper}</Typography>
      ) : null}
      {field}
    </Box>
  )

  return (
    <>
      {hasAcademies
        ? fieldGroup(
          'Host',
          'Who is hosting this event?',
          <FormControl component="fieldset" fullWidth>
            <RadioGroup
              className={hostOptionClasses.optionsColumn}
              value={isAcademyEvent ? 'coachingCenter' : 'individual'}
              onChange={handleHostChange}
            >
              {HOST_OPTIONS.map(option => (
                <FormControlLabel
                  key={option.value}
                  value={option.value}
                  className={hostOptionClasses.optionLabel}
                  control={
                    <Radio
                      className={hostOptionClasses.radio}
                      color="primary"
                      data-cy={option.value === 'coachingCenter' ? 'yes' : 'no'}
                    />
                  }
                  label={
                    <Box>
                      <Typography className={hostOptionClasses.optionTitle}>
                        {option.title}
                      </Typography>
                      <Typography
                        className={hostOptionClasses.optionDescription}
                      >
                        {option.description}
                      </Typography>
                    </Box>
                  }
                />
              ))}
            </RadioGroup>
          </FormControl>,
        )
        : null}
      {isAcademyEvent
        ? fieldGroup(
          'Coaching center',
          'Select the coaching center hosting this event.',
          <>
            <FormControl
              error={!!errors.academyId}
              variant="outlined"
              fullWidth
              className={classes.fieldInput}
            >
              <InputLabel id="create-event-academy-label">
                Coaching center
              </InputLabel>
              <Controller
                name="academyId"
                control={control}
                render={({ onChange, value }) => (
                  <Select
                    labelId="create-event-academy-label"
                    label="Coaching center"
                    value={value || ''}
                    onChange={onChange}
                    data-cy="academy"
                  >
                    {coachAcademies.map(academy => (
                      <MenuItem
                        key={academy.id}
                        value={academy.id}
                        data-cy="academy-menu-item"
                      >
                        {academy.name}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
            </FormControl>
            {errors.academyId ? (
              <Typography variant="caption" color="error">
                Please select a coaching center
              </Typography>
            ) : null}
          </>,
        )
        : null}
      {((isAcademyEvent && academyId) || !isAcademyEvent) &&
        fieldGroup(
          'Facility',
          'Select where this event will be held.',
          <>
            <FormControl
              error={!!errors.locationId}
              variant="outlined"
              fullWidth
              className={classes.fieldInput}
            >
              <InputLabel id="create-event-location-label">Facility</InputLabel>
              <Controller
                name="locationId"
                control={control}
                render={({ onChange, value }) => (
                  <Select
                    labelId="create-event-location-label"
                    label="Facility"
                    value={value || ''}
                    onChange={onChange}
                    data-cy="location"
                  >
                    {facilities
                      .slice()
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map(facility => (
                        <MenuItem
                          key={facility.id}
                          value={facility.id}
                          data-cy="facility-menu-item"
                        >
                          {facility.name}
                        </MenuItem>
                      ))}
                  </Select>
                )}
              />
            </FormControl>
            {errors.locationId ? (
              <Typography variant="caption" color="error">
                Please select a location
              </Typography>
            ) : null}
          </>,
        )}
    </>
  )
}

export default CreateEventLocation
